import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AdminLayout from './AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  ArrowLeft, Save, Plus, Trash2, GripVertical, Download, FileSpreadsheet,
  FileText, Mail, MessageCircle, Send, Loader2, CheckCircle2, AlertTriangle,
  Link2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VAT_RATE = 0.15;

const fmtZar = (n) =>
  `R${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ---------------------------------------------------------------------------
// AdminQuoteEditor — Phase 1C: editable quote builder with drag-to-reorder
//                  + Phase 1D: XLSX/PDF download, Email & WhatsApp delivery.
// ---------------------------------------------------------------------------
const AdminQuoteEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

  const [quote, setQuote] = useState(null);
  const [trades, setTrades] = useState([]);
  const [materialRates, setMaterialRates] = useState([]);
  const [laborRates, setLaborRates] = useState([]);
  const [saving, setSaving] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emailDialog, setEmailDialog] = useState(false);
  const [emailRecipient, setEmailRecipient] = useState('');
  const [whatsappOpen, setWhatsappOpen] = useState(false);
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [whatsappData, setWhatsappData] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // --- Load -----------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [q, meta] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/admin/quotes/${id}`, { headers: authHeaders() }),
          axios.get(`${BACKEND_URL}/api/admin/quotes/meta/trades`, { headers: authHeaders() }),
        ]);
        if (cancelled) return;
        setQuote(q.data);
        setTrades(meta.data.trades || []);
        setEmailRecipient(q.data.client_email || '');
        setWhatsappPhone(q.data.client_phone || '');
        // Now load rate libraries for the quote's trade
        const t = q.data.trade;
        const [mr, lr] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/admin/quotes/material-rates?trade=${t}`, { headers: authHeaders() }),
          axios.get(`${BACKEND_URL}/api/admin/quotes/labor-rates?trade=${t}`, { headers: authHeaders() }),
        ]);
        if (cancelled) return;
        setMaterialRates(mr.data || []);
        setLaborRates(lr.data || []);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Could not load quote');
        navigate('/admin/quotes');
      }
    })();
    return () => { cancelled = true; };
  }, [id, navigate]);

  // --- Totals (recomputed locally on every edit) ----------------------------
  const totals = useMemo(() => {
    if (!quote) return { materials_total: 0, labor_total: 0, subtotal: 0, vat_amount: 0, total: 0 };
    const m = (quote.material_lines || []).reduce(
      (s, l) => s + Number(l.qty || 0) * Number(l.unit_price_zar || 0), 0);
    const lab = (quote.labor_lines || []).reduce(
      (s, l) => s + Number(l.hours || 0) * Number(l.hourly_rate_zar || 0), 0);
    const sub = m + lab;
    const vat = sub * (quote.vat_rate ?? VAT_RATE);
    return {
      materials_total: m,
      labor_total: lab,
      subtotal: sub,
      vat_amount: vat,
      total: sub + vat,
    };
  }, [quote]);

  const update = (patch) => setQuote((q) => ({ ...q, ...patch }));

  // --- Material line ops ----------------------------------------------------
  const setMaterialLines = (lines) => update({ material_lines: lines });
  const updateMaterialLine = (idx, patch) => {
    const next = [...(quote.material_lines || [])];
    next[idx] = { ...next[idx], ...patch };
    setMaterialLines(next);
  };
  const removeMaterialLine = (idx) => {
    setMaterialLines((quote.material_lines || []).filter((_, i) => i !== idx));
  };
  const addMaterialLine = (rateId) => {
    const rate = materialRates.find((r) => r.id === rateId);
    const line = rate
      ? {
          material_rate_id: rate.id,
          name: rate.name,
          unit: rate.unit,
          qty: 1,
          unit_price_zar: rate.unit_price_zar,
        }
      : { material_rate_id: null, name: '', unit: 'each', qty: 1, unit_price_zar: 0 };
    setMaterialLines([...(quote.material_lines || []), line]);
  };

  // --- Labor line ops -------------------------------------------------------
  const setLaborLines = (lines) => update({ labor_lines: lines });
  const updateLaborLine = (idx, patch) => {
    const next = [...(quote.labor_lines || [])];
    next[idx] = { ...next[idx], ...patch };
    setLaborLines(next);
  };
  const removeLaborLine = (idx) => {
    setLaborLines((quote.labor_lines || []).filter((_, i) => i !== idx));
  };
  const addLaborLine = (rateId) => {
    const rate = laborRates.find((r) => r.id === rateId);
    const line = rate
      ? {
          labor_rate_id: rate.id,
          role: rate.role,
          hours: 8,
          hourly_rate_zar: rate.hourly_rate_zar,
        }
      : { labor_rate_id: null, role: '', hours: 8, hourly_rate_zar: 0 };
    setLaborLines([...(quote.labor_lines || []), line]);
  };

  // --- Save -----------------------------------------------------------------
  const handleSave = async (extraPatch = {}) => {
    setSaving(true);
    try {
      const payload = {
        title: quote.title,
        client_name: quote.client_name || null,
        client_email: quote.client_email || null,
        client_phone: quote.client_phone || null,
        trade: quote.trade,
        source_files: quote.source_files || [],
        extracted_data: quote.extracted_data || null,
        material_lines: (quote.material_lines || []).map((l) => ({
          material_rate_id: l.material_rate_id || null,
          name: l.name,
          unit: l.unit,
          qty: Number(l.qty || 0),
          unit_price_zar: Number(l.unit_price_zar || 0),
        })),
        labor_lines: (quote.labor_lines || []).map((l) => ({
          labor_rate_id: l.labor_rate_id || null,
          role: l.role,
          hours: Number(l.hours || 0),
          hourly_rate_zar: Number(l.hourly_rate_zar || 0),
        })),
        vat_rate: quote.vat_rate ?? VAT_RATE,
        notes: quote.notes || null,
        ...extraPatch,
      };
      const res = await axios.put(
        `${BACKEND_URL}/api/admin/quotes/${id}`,
        payload,
        { headers: authHeaders() },
      );
      setQuote(res.data);
      toast.success('Quote saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // --- Status ---------------------------------------------------------------
  const setStatus = async (status) => {
    try {
      await axios.patch(
        `${BACKEND_URL}/api/admin/quotes/${id}/status`,
        { status },
        { headers: authHeaders() },
      );
      setQuote((q) => ({ ...q, status }));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error('Status update failed');
    }
  };

  // --- Downloads ------------------------------------------------------------
  const download = async (kind) => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/quotes/${id}/${kind}`,
        { headers: authHeaders(), responseType: 'blob' },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      const safe = (quote.title || 'quote').replace(/[^a-z0-9_\-]+/gi, '_').slice(0, 60);
      a.download = `${safe}_${id.slice(0, 8)}.${kind}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error(`${kind.toUpperCase()} download failed`);
    }
  };

  // --- Email ----------------------------------------------------------------
  const handleSendEmail = async () => {
    if (!emailRecipient || !emailRecipient.includes('@')) {
      toast.error('Enter a valid recipient email');
      return;
    }
    setEmailing(true);
    try {
      // Save first to ensure backend has latest
      await handleSave();
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/quotes/${id}/send-email`,
        { recipient: emailRecipient, share_origin: window.location.origin },
        { headers: authHeaders() },
      );
      if (res.data?.ok) {
        toast.success(`Quote emailed to ${res.data.recipient}`);
        setEmailDialog(false);
        // Refresh quote (status may have flipped to 'sent')
        const refreshed = await axios.get(
          `${BACKEND_URL}/api/admin/quotes/${id}`,
          { headers: authHeaders() },
        );
        setQuote(refreshed.data);
      } else {
        toast.error('Email send failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Email send failed');
    } finally {
      setEmailing(false);
    }
  };

  // --- WhatsApp -------------------------------------------------------------
  const openWhatsapp = async () => {
    if (!whatsappPhone) {
      toast.error('Enter a phone number');
      return;
    }
    try {
      // Save first
      await handleSave();
      const res = await axios.get(
        `${BACKEND_URL}/api/admin/quotes/${id}/whatsapp-link?phone=${encodeURIComponent(whatsappPhone)}&share_origin=${encodeURIComponent(window.location.origin)}`,
        { headers: authHeaders() },
      );
      setWhatsappData(res.data);
      // Open WhatsApp in a new tab
      window.open(res.data.url, '_blank', 'noopener,noreferrer');
      toast.success('WhatsApp opened in new tab');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'WhatsApp link generation failed');
    }
  };

  // --- Delete ---------------------------------------------------------------
  const handleDelete = async () => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/quotes/${id}`, { headers: authHeaders() });
      toast.success('Quote deleted');
      navigate('/admin/quotes');
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  // --- DnD ------------------------------------------------------------------
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const onMaterialDrag = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = (quote.material_lines || []).map((_, i) => `m-${i}`);
    const oldIdx = ids.indexOf(active.id);
    const newIdx = ids.indexOf(over.id);
    if (oldIdx >= 0 && newIdx >= 0) {
      setMaterialLines(arrayMove(quote.material_lines, oldIdx, newIdx));
    }
  };
  const onLaborDrag = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ids = (quote.labor_lines || []).map((_, i) => `l-${i}`);
    const oldIdx = ids.indexOf(active.id);
    const newIdx = ids.indexOf(over.id);
    if (oldIdx >= 0 && newIdx >= 0) {
      setLaborLines(arrayMove(quote.labor_lines, oldIdx, newIdx));
    }
  };

  if (!quote) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#00CED1' }} />
        </div>
      </AdminLayout>
    );
  }

  const statusColors = {
    draft: { border: '#9ca3af', bg: 'rgba(156,163,175,0.1)', text: '#525252' },
    sent: { border: '#0891b2', bg: 'rgba(8,145,178,0.1)', text: '#0891b2' },
    accepted: { border: '#16a34a', bg: 'rgba(22,163,74,0.1)', text: '#16a34a' },
    rejected: { border: '#dc2626', bg: 'rgba(220,38,38,0.1)', text: '#dc2626' },
  };
  const sc = statusColors[quote.status || 'draft'];

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/quotes')}
            data-testid="admin-quote-editor-back"
          >
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold" data-testid="admin-quote-editor-heading">{quote.title}</h1>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span
                className="text-xs px-2 py-0.5 rounded-full border font-semibold uppercase"
                style={{ color: sc.text, borderColor: sc.border, backgroundColor: sc.bg }}
                data-testid="admin-quote-editor-status"
              >
                {quote.status || 'draft'}
              </span>
              <span className="text-xs text-gray-500">#{id.slice(0, 8).toUpperCase()}</span>
              <span className="text-xs text-gray-500">· {(quote.created_at || '').slice(0, 10)}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={() => handleSave()}
            disabled={saving}
            className="text-white font-semibold"
            style={{ backgroundColor: '#00CED1' }}
            data-testid="admin-quote-editor-save"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Save
          </Button>
          <Button variant="outline" onClick={() => download('xlsx')} data-testid="admin-quote-editor-xlsx">
            <FileSpreadsheet className="h-4 w-4 mr-2" />XLSX
          </Button>
          <Button variant="outline" onClick={() => download('pdf')} data-testid="admin-quote-editor-pdf">
            <FileText className="h-4 w-4 mr-2" />PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              const url = `${window.location.origin}/q/${quote.share_token}`;
              navigator.clipboard.writeText(url).then(
                () => toast.success('Public share link copied'),
                () => toast.error('Copy failed — link is: ' + url),
              );
            }}
            data-testid="admin-quote-editor-share-link"
            title="Copy public client-facing link"
          >
            <Link2 className="h-4 w-4 mr-2" />Share link
          </Button>
          <Button
            variant="outline"
            onClick={() => setEmailDialog(true)}
            data-testid="admin-quote-editor-email"
          >
            <Mail className="h-4 w-4 mr-2" />Email
          </Button>
          <Button
            variant="outline"
            onClick={() => setWhatsappOpen(true)}
            className="border-green-500 text-green-600 hover:bg-green-50"
            data-testid="admin-quote-editor-whatsapp"
          >
            <MessageCircle className="h-4 w-4 mr-2" />WhatsApp
          </Button>
        </div>
      </div>

      {/* Status quick-actions */}
      <div className="flex items-center gap-2 mb-6 flex-wrap">
        <span className="text-xs text-gray-500">Status:</span>
        {['draft', 'sent', 'accepted', 'rejected'].map((s) => (
          <button
            key={s}
            type="button"
            onClick={() => setStatus(s)}
            className={`text-xs px-2.5 py-1 rounded-full border capitalize ${
              quote.status === s
                ? 'font-semibold'
                : 'text-gray-500 hover:text-gray-800 border-gray-300'
            }`}
            style={
              quote.status === s
                ? { color: statusColors[s].text, borderColor: statusColors[s].border, backgroundColor: statusColors[s].bg }
                : undefined
            }
            data-testid={`admin-quote-editor-status-${s}`}
          >
            {s}
          </button>
        ))}
      </div>

      {/* Client + meta + AI summary */}
      <Card className="mb-6">
        <CardContent className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs">Quote title</Label>
            <Input
              value={quote.title || ''}
              onChange={(e) => update({ title: e.target.value })}
              data-testid="admin-quote-editor-title"
            />
          </div>
          <div>
            <Label className="text-xs">Trade</Label>
            <Input value={quote.trade || ''} readOnly className="bg-gray-50" />
          </div>
          <div>
            <Label className="text-xs">Client name</Label>
            <Input
              value={quote.client_name || ''}
              onChange={(e) => update({ client_name: e.target.value })}
              data-testid="admin-quote-editor-client-name"
            />
          </div>
          <div>
            <Label className="text-xs">Client email</Label>
            <Input
              type="email"
              value={quote.client_email || ''}
              onChange={(e) => update({ client_email: e.target.value })}
              data-testid="admin-quote-editor-client-email"
            />
          </div>
          <div>
            <Label className="text-xs">Client phone</Label>
            <Input
              value={quote.client_phone || ''}
              onChange={(e) => update({ client_phone: e.target.value })}
              placeholder="+27 82 123 4567"
              data-testid="admin-quote-editor-client-phone"
            />
          </div>
          <div>
            <Label className="text-xs">Notes (printed on quote)</Label>
            <Textarea
              rows={2}
              value={quote.notes || ''}
              onChange={(e) => update({ notes: e.target.value })}
              data-testid="admin-quote-editor-notes"
            />
          </div>
        </CardContent>
      </Card>

      {quote.extracted_data?.plan_summary && (
        <Card className="mb-6 border-dashed" style={{ backgroundColor: 'rgba(0,206,209,0.04)' }}>
          <CardContent className="p-4 text-sm">
            <p className="text-xs uppercase font-semibold text-gray-500 mb-1">AI plan summary</p>
            <p className="text-gray-700">{quote.extracted_data.plan_summary}</p>
            {quote.extracted_data?.missing_items?.length > 0 && (
              <div className="mt-2 text-xs text-amber-700">
                <strong>Items the AI couldn't price:</strong> {quote.extracted_data.missing_items.join(' · ')}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* MATERIALS */}
      <Section title="Material lines" testid="admin-quote-editor-materials">
        <RatePicker
          rates={materialRates}
          placeholder="Add material from library…"
          onPick={(id) => addMaterialLine(id)}
          testid="admin-quote-editor-add-material"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2 mb-3"
          onClick={() => addMaterialLine(null)}
          data-testid="admin-quote-editor-add-material-blank"
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add custom line
        </Button>
        {(quote.material_lines || []).length === 0 ? (
          <EmptyState text="No material lines yet. Pick one from your library above." />
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onMaterialDrag}>
            <SortableContext
              items={(quote.material_lines || []).map((_, i) => `m-${i}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="overflow-x-auto bg-white rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="w-8" />
                      <th className="px-3 py-2 text-left">Description</th>
                      <th className="px-3 py-2 text-right w-24">Qty</th>
                      <th className="px-3 py-2 text-left w-20">Unit</th>
                      <th className="px-3 py-2 text-right w-32">Unit price</th>
                      <th className="px-3 py-2 text-right w-32">Line total</th>
                      <th className="w-12" />
                    </tr>
                  </thead>
                  <tbody>
                    {(quote.material_lines || []).map((line, idx) => (
                      <SortableMaterialRow
                        key={`m-${idx}`}
                        id={`m-${idx}`}
                        line={line}
                        onChange={(patch) => updateMaterialLine(idx, patch)}
                        onRemove={() => removeMaterialLine(idx)}
                        testidIdx={idx}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </SortableContext>
          </DndContext>
        )}
      </Section>

      {/* LABOR */}
      <Section title="Labor lines" testid="admin-quote-editor-labor">
        <RatePicker
          rates={laborRates}
          placeholder="Add role from library…"
          labelKey="role"
          rateKey="hourly_rate_zar"
          unitSuffix="/h"
          onPick={(id) => addLaborLine(id)}
          testid="admin-quote-editor-add-labor"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mt-2 mb-3"
          onClick={() => addLaborLine(null)}
          data-testid="admin-quote-editor-add-labor-blank"
        >
          <Plus className="h-3.5 w-3.5 mr-1" /> Add custom line
        </Button>
        {(quote.labor_lines || []).length === 0 ? (
          <EmptyState text="No labor lines yet. Pick a role from your library above." />
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onLaborDrag}>
            <SortableContext
              items={(quote.labor_lines || []).map((_, i) => `l-${i}`)}
              strategy={verticalListSortingStrategy}
            >
              <div className="overflow-x-auto bg-white rounded-lg border">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                    <tr>
                      <th className="w-8" />
                      <th className="px-3 py-2 text-left">Role</th>
                      <th className="px-3 py-2 text-right w-24">Hours</th>
                      <th className="px-3 py-2 text-right w-32">Hourly rate</th>
                      <th className="px-3 py-2 text-right w-32">Line total</th>
                      <th className="w-12" />
                    </tr>
                  </thead>
                  <tbody>
                    {(quote.labor_lines || []).map((line, idx) => (
                      <SortableLaborRow
                        key={`l-${idx}`}
                        id={`l-${idx}`}
                        line={line}
                        onChange={(patch) => updateLaborLine(idx, patch)}
                        onRemove={() => removeLaborLine(idx)}
                        testidIdx={idx}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </SortableContext>
          </DndContext>
        )}
      </Section>

      {/* Totals */}
      <Card className="mb-8 mt-2" style={{ backgroundColor: '#F0FDFD', borderColor: '#A5F3F2' }}>
        <CardContent className="p-5">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
            <TotalCell label="Materials" value={totals.materials_total} />
            <TotalCell label="Labor" value={totals.labor_total} />
            <TotalCell label="Subtotal" value={totals.subtotal} />
            <TotalCell label="VAT (15%)" value={totals.vat_amount} />
            <TotalCell label="Total" value={totals.total} bold />
          </div>
        </CardContent>
      </Card>

      {/* Delete */}
      <div className="text-right">
        <Button
          variant="ghost"
          className="text-red-600 hover:bg-red-50"
          onClick={() => setConfirmDelete(true)}
          data-testid="admin-quote-editor-delete"
        >
          <Trash2 className="h-4 w-4 mr-2" /> Delete quote
        </Button>
      </div>

      {/* Email dialog */}
      <Dialog open={emailDialog} onOpenChange={(o) => !o && !emailing && setEmailDialog(false)}>
        <DialogContent data-testid="admin-quote-editor-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5" style={{ color: '#00CED1' }} /> Email this quote
            </DialogTitle>
            <DialogDescription>
              The branded PDF will be attached and sent via your SendGrid integration.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Recipient email</Label>
              <Input
                type="email"
                value={emailRecipient}
                onChange={(e) => setEmailRecipient(e.target.value)}
                placeholder="client@example.com"
                data-testid="admin-quote-editor-email-recipient"
              />
            </div>
            <div className="text-xs text-gray-500 bg-gray-50 rounded p-3 leading-relaxed">
              <strong>Subject:</strong> Your quotation — {quote.title}<br />
              <strong>Body:</strong> Standard SA-style cover letter referencing total {fmtZar(totals.total)} incl. VAT.
              The recipient is set to <strong>{emailRecipient || '—'}</strong>.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEmailDialog(false)} disabled={emailing}>
              Cancel
            </Button>
            <Button
              onClick={handleSendEmail}
              disabled={emailing || !emailRecipient.includes('@')}
              className="text-white font-semibold"
              style={{ backgroundColor: '#00CED1' }}
              data-testid="admin-quote-editor-email-send"
            >
              {emailing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Send email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* WhatsApp dialog */}
      <Dialog open={whatsappOpen} onOpenChange={(o) => !o && setWhatsappOpen(false)}>
        <DialogContent data-testid="admin-quote-editor-whatsapp-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-green-600" /> WhatsApp this quote
            </DialogTitle>
            <DialogDescription>
              We'll open WhatsApp (web or app) with a prefilled message containing the quote summary. The client can reply to request the full PDF.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Phone number (with country code)</Label>
              <Input
                value={whatsappPhone}
                onChange={(e) => setWhatsappPhone(e.target.value)}
                placeholder="+27 82 123 4567"
                data-testid="admin-quote-editor-whatsapp-phone"
              />
            </div>
            {whatsappData?.message_preview && (
              <div className="text-xs bg-green-50 border border-green-200 rounded p-3 whitespace-pre-line">
                <p className="text-[10px] font-semibold text-green-700 uppercase mb-1">Preview</p>
                {whatsappData.message_preview}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWhatsappOpen(false)}>Cancel</Button>
            <Button
              onClick={openWhatsapp}
              className="bg-green-600 hover:bg-green-700 text-white font-semibold"
              data-testid="admin-quote-editor-whatsapp-open"
            >
              <MessageCircle className="h-4 w-4 mr-2" />Open WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete this quote?"
        description={`"${quote.title}" will be permanently removed. This cannot be undone.`}
        confirmLabel="Delete"
        destructive
        onCancel={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        testId="admin-quote-editor-confirm-delete"
      />
    </AdminLayout>
  );
};

// ---------------------------------------------------------------------------
// Subcomponents
// ---------------------------------------------------------------------------
const Section = ({ title, children, testid }) => (
  <section className="mb-6" data-testid={testid}>
    <h2 className="text-lg font-semibold mb-2">{title}</h2>
    {children}
  </section>
);

const EmptyState = ({ text }) => (
  <div className="border-2 border-dashed rounded-lg p-6 text-center text-sm text-gray-500">{text}</div>
);

const TotalCell = ({ label, value, bold }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wide text-teal-700">{label}</p>
    <p className={`font-mono ${bold ? 'text-lg font-bold text-gray-900' : 'text-gray-800'}`}>
      {fmtZar(value)}
    </p>
  </div>
);

const RatePicker = ({ rates, placeholder, onPick, testid, labelKey = 'name', rateKey = 'unit_price_zar', unitSuffix = '' }) => {
  const [value, setValue] = useState('');
  if (!rates || rates.length === 0) {
    return (
      <p className="text-xs text-gray-500 italic">
        No rates configured for this trade — add some in the Rate library tab first.
      </p>
    );
  }
  return (
    <Select
      value={value}
      onValueChange={(v) => {
        setValue('');
        onPick(v);
      }}
    >
      <SelectTrigger className="max-w-md" data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {rates.map((r) => (
          <SelectItem key={r.id} value={r.id}>
            {r[labelKey]} · {fmtZar(r[rateKey])}{unitSuffix}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const SortableMaterialRow = ({ id, line, onChange, onRemove, testidIdx }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const lineTotal = Number(line.qty || 0) * Number(line.unit_price_zar || 0);
  return (
    <tr ref={setNodeRef} style={style} className="border-t" data-testid={`admin-quote-editor-material-row-${testidIdx}`}>
      <td className="px-2 py-1.5 text-center cursor-grab text-gray-400" {...attributes} {...listeners}>
        <GripVertical className="h-4 w-4 mx-auto" />
      </td>
      <td className="px-3 py-1.5">
        <Input
          value={line.name || ''}
          onChange={(e) => onChange({ name: e.target.value })}
          className="h-8 text-sm"
          data-testid={`admin-quote-editor-material-name-${testidIdx}`}
        />
      </td>
      <td className="px-3 py-1.5">
        <Input
          type="number"
          step="0.01"
          min="0"
          value={line.qty ?? 0}
          onChange={(e) => onChange({ qty: parseFloat(e.target.value) || 0 })}
          className="h-8 text-sm text-right font-mono"
          data-testid={`admin-quote-editor-material-qty-${testidIdx}`}
        />
      </td>
      <td className="px-3 py-1.5">
        <Input
          value={line.unit || ''}
          onChange={(e) => onChange({ unit: e.target.value })}
          className="h-8 text-sm"
        />
      </td>
      <td className="px-3 py-1.5">
        <Input
          type="number"
          step="0.01"
          min="0"
          value={line.unit_price_zar ?? 0}
          onChange={(e) => onChange({ unit_price_zar: parseFloat(e.target.value) || 0 })}
          className="h-8 text-sm text-right font-mono"
          data-testid={`admin-quote-editor-material-price-${testidIdx}`}
        />
      </td>
      <td className="px-3 py-1.5 text-right font-mono font-semibold" data-testid={`admin-quote-editor-material-total-${testidIdx}`}>
        {fmtZar(lineTotal)}
      </td>
      <td className="px-2 py-1.5 text-center">
        <button
          type="button"
          onClick={onRemove}
          className="text-gray-400 hover:text-red-600 p-1"
          data-testid={`admin-quote-editor-material-remove-${testidIdx}`}
          title="Remove line"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
};

const SortableLaborRow = ({ id, line, onChange, onRemove, testidIdx }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const lineTotal = Number(line.hours || 0) * Number(line.hourly_rate_zar || 0);
  return (
    <tr ref={setNodeRef} style={style} className="border-t" data-testid={`admin-quote-editor-labor-row-${testidIdx}`}>
      <td className="px-2 py-1.5 text-center cursor-grab text-gray-400" {...attributes} {...listeners}>
        <GripVertical className="h-4 w-4 mx-auto" />
      </td>
      <td className="px-3 py-1.5">
        <Input
          value={line.role || ''}
          onChange={(e) => onChange({ role: e.target.value })}
          className="h-8 text-sm"
          data-testid={`admin-quote-editor-labor-role-${testidIdx}`}
        />
      </td>
      <td className="px-3 py-1.5">
        <Input
          type="number"
          step="0.5"
          min="0"
          value={line.hours ?? 0}
          onChange={(e) => onChange({ hours: parseFloat(e.target.value) || 0 })}
          className="h-8 text-sm text-right font-mono"
          data-testid={`admin-quote-editor-labor-hours-${testidIdx}`}
        />
      </td>
      <td className="px-3 py-1.5">
        <Input
          type="number"
          step="0.01"
          min="0"
          value={line.hourly_rate_zar ?? 0}
          onChange={(e) => onChange({ hourly_rate_zar: parseFloat(e.target.value) || 0 })}
          className="h-8 text-sm text-right font-mono"
          data-testid={`admin-quote-editor-labor-rate-${testidIdx}`}
        />
      </td>
      <td className="px-3 py-1.5 text-right font-mono font-semibold" data-testid={`admin-quote-editor-labor-total-${testidIdx}`}>
        {fmtZar(lineTotal)}
      </td>
      <td className="px-2 py-1.5 text-center">
        <button
          type="button"
          onClick={onRemove}
          className="text-gray-400 hover:text-red-600 p-1"
          data-testid={`admin-quote-editor-labor-remove-${testidIdx}`}
          title="Remove line"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
};

export default AdminQuoteEditor;
